import {
  type FrontendInstaller,
  otherInstalledIntegration,
} from "@p0security/integrations-installer";
import type { ServiceNowAiComponents } from "@p0security/integrations-servicenow-ai-shared/types";
import type { ItemConfigOf } from "@p0security/shared/install/types";
import { CommandDisplay, Typography } from "@p0security/ui";
import type React from "react";

import {
  type InstallContext,
  awsStoreInstallSecret,
  awsTf,
  gcpStoreInstallSecret,
  gcpTf,
} from "./iac.ts";

type SecretManagerConfig = ItemConfigOf<
  ServiceNowAiComponents["instance"]
>["secretManager"];

type SecretManagerType = SecretManagerConfig["type"];

type InstallCommands = ReturnType<
  NonNullable<
    NonNullable<
      FrontendInstaller<ServiceNowAiComponents>["components"]["instance"]
    >["instructions"]
  >
>["commands"];

/** What a secret manager contributes to the instance install steps. Methods,
 * not properties, let an implementation narrow to its own member. */
type InstanceIac<T extends SecretManagerType = SecretManagerType> = {
  commands(
    context: InstallContext,
    instance: string,
    secretManager: Extract<SecretManagerConfig, { type: T }>
  ): InstallCommands;
  /** The command that stores the install secret, if this secret manager has one */
  storeInstallSecret(
    instance: string,
    secretManager: Extract<SecretManagerConfig, { type: T }>
  ): string | undefined;
};

const gcpSmIac: InstanceIac = {
  commands: (context, instance, secretManager) => ({
    iac: [
      {
        header:
          'Apply the following terraform to deploy P0\'s ServiceNow connector, then click "Next".',
        command: gcpTf(context, instance, secretManager),
      },
    ],
  }),
  storeInstallSecret: gcpStoreInstallSecret,
} satisfies InstanceIac<"gcp-sm">;

const awsSmIac: InstanceIac = {
  commands: (context, instance, secretManager) => ({
    iac: [
      {
        header:
          'Apply the following terraform to deploy P0\'s ServiceNow connector, then click "Next".',
        command: awsTf(context, instance, secretManager),
      },
    ],
  }),
  storeInstallSecret: awsStoreInstallSecret,
} satisfies InstanceIac<"aws-sm">;

// The Azure Key Vault option is hidden, and the backend refuses it, until the
// connector runs on Azure.
const azureKvIac: InstanceIac = {
  commands: () => ({}),
  storeInstallSecret: () => undefined,
} satisfies InstanceIac<"azure-kv">;

const instanceIac: Record<SecretManagerType, InstanceIac> = {
  "aws-sm": awsSmIac,
  "azure-kv": azureKvIac,
  "gcp-sm": gcpSmIac,
};

const { Paragraph } = Typography;

const InstanceSetup: React.FC = () => (
  <Paragraph>
    Agents get ServiceNow machine users that P0 creates, one for each agent
    session, and grants roles to. Before you continue, set up the instance:
    <ol className="preamble-list">
      <li>
        In System Properties, set{" "}
        <code>glide.oauth.inbound.client.credential.grant_type.enabled</code> to{" "}
        <code>true</code> in the Global scope.
      </li>
      <li>
        Make a machine user, with web service access only and the{" "}
        <code>user_admin</code> role and no other role.
      </li>
      <li>
        In Machine Identity Console, add an inbound integration of type
        &quot;OAuth - Client credential grant&quot; whose OAuth application user
        is that user. This is the provisioner.
      </li>
      <li>
        Make an RSA key pair. Save the private key as{" "}
        <code>p0-servicenow-signing-key.pem</code>, and add its certificate as a
        trust store certificate.
      </li>
      <li>
        Add an inbound integration of type &quot;OAuth - JWT bearer grant&quot;
        with a verifier map for that certificate, and the user field User ID.
      </li>
      <li>
        Give both integrations an auth scope that covers the Table API, for
        example <code>useraccount</code>.
      </li>
    </ol>
    P0&apos;s connector runs in your AWS account or Google Cloud project and
    holds both client secrets and the signing key, so P0 never sees them.
  </Paragraph>
);

const InstallSecretSetup: React.FC<{
  id: string;
  secretManager: SecretManagerConfig;
}> = ({ id, secretManager }) => {
  const storeInstallSecret = instanceIac[secretManager.type].storeInstallSecret(
    id,
    secretManager
  );
  return (
    <div>
      {storeInstallSecret && (
        <>
          <Paragraph type="secondary">
            Set <code>SN_PROVISIONER_CLIENT_SECRET</code> and{" "}
            <code>SN_JWT_CLIENT_SECRET</code> to the two integrations&apos;
            client secrets, put the signing key in{" "}
            <code>p0-servicenow-signing-key.pem</code>, and run this command to
            store them. The connector cannot reach the instance until the secret
            holds them.
          </Paragraph>
          <CommandDisplay commands={storeInstallSecret} />
        </>
      )}
      <Paragraph type="secondary">
        Then enter the integrations&apos; client IDs and the signing key&apos;s
        ID.
      </Paragraph>
    </div>
  );
};

export const serviceNowAiInstaller: FrontendInstaller<ServiceNowAiComponents> =
  {
    components: {
      instance: {
        instructions: (context, id, item) => ({
          help: <InstanceSetup />,
          commands: item?.secretManager
            ? instanceIac[item.secretManager.type].commands(
                context,
                id,
                item.secretManager
              )
            : {},
        }),
        items: {
          provisionerClientId: {
            labeler: (_, id, { secretManager, state }) =>
              state === "configure" && (
                <InstallSecretSetup id={id} secretManager={secretManager} />
              ),
          },
          secretManager: {
            options: {
              "aws-sm": {
                items: {
                  install: {
                    optionProvider: otherInstalledIntegration({
                      integration: "aws-sm",
                      component: "iam-write",
                    }),
                  },
                },
              },
              "gcp-sm": {
                items: {
                  install: {
                    optionProvider: otherInstalledIntegration({
                      integration: "gcp-sm",
                      component: "iam-write",
                    }),
                  },
                },
              },
            },
          },
        },
      },
    },
  };
